import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, doc, updateDoc } from 'firebase/firestore';
import { 
    FaCheckCircle, FaClock, FaTimesCircle, FaSpinner, 
    FaSearch, FaFileInvoiceDollar, FaCalendarAlt, FaSave 
} from 'react-icons/fa';
import { 
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area 
} from 'recharts';

const CompanyBillingPage = () => {
    const { userData } = useAuth();
    const [rentals, setRentals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRental, setSelectedRental] = useState(null);
    const [notes, setNotes] = useState('');
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('Tümü');
    const [dateFilter, setDateFilter] = useState('all'); // 'all', 'thisMonth', 'lastMonth', 'thisYear'

    // Chart Data
    const [monthlyData, setMonthlyData] = useState([]);

    const fetchRentals = useCallback(async () => {
        if (!userData?.companyProfileId) return;
        setLoading(true);
        try {
            const q = query(
                collection(db, 'rentals'), 
                where('companyId', '==', userData.companyProfileId),
                orderBy('rentalStartDate', 'desc')
            );
            const querySnapshot = await getDocs(q);
            const rentalsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRentals(rentalsData);
            
            // Process Chart Data (Aggregate by Month)
            const monthlyStats = {};
            const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
            
            rentalsData.forEach(rental => {
                if (rental.rentalStartDate) {
                    const date = new Date(rental.rentalStartDate.seconds * 1000);
                    // Use YYYY-MM for sorting key
                    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                    const label = `${months[date.getMonth()]} ${date.getFullYear()}`;
                    
                    if (!monthlyStats[key]) monthlyStats[key] = { key, label, amount: 0 };
                    monthlyStats[key].amount += Number(rental.totalPrice) || 0;
                }
            });

            // Convert to array and sort
            const chartData = Object.values(monthlyStats)
                .sort((a, b) => a.key.localeCompare(b.key))
                .slice(-12); // Last 12 months

            setMonthlyData(chartData);
        } catch (error) {
            console.error("Billing Error:", error);
        } finally {
            setLoading(false);
        }
    }, [userData]);

    useEffect(() => {
        fetchRentals();
    }, [fetchRentals]);

    // Calculate Billing Statistics
    const stats = useMemo(() => {
        const total = rentals.reduce((sum, r) => sum + (Number(r.totalPrice) || 0), 0);
        const paid = rentals.filter(r => r.paymentStatus === 'Ödendi').reduce((sum, r) => sum + (Number(r.totalPrice) || 0), 0);
        const pending = rentals.filter(r => r.paymentStatus !== 'Ödendi').reduce((sum, r) => sum + (Number(r.totalPrice) || 0), 0);
        return { total, paid, pending, count: rentals.length };
    }, [rentals]);

    // Filter Logic
    const filteredRentals = rentals.filter(rental => {
        const matchesSearch = (rental.vehiclePlate || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (rental.renterName || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'Tümü' || rental.paymentStatus === filterStatus;
        
        let matchesDate = true;
        if (dateFilter !== 'all' && rental.rentalStartDate) {
            const date = new Date(rental.rentalStartDate.seconds * 1000);
            const now = new Date();
            if (dateFilter === 'thisMonth') {
                matchesDate = date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
            } else if (dateFilter === 'lastMonth') {
                const lastMonth = new Date();
                lastMonth.setMonth(now.getMonth() - 1);
                matchesDate = date.getMonth() === lastMonth.getMonth() && date.getFullYear() === lastMonth.getFullYear();
            } else if (dateFilter === 'thisYear') {
                matchesDate = date.getFullYear() === now.getFullYear();
            }
        }

        return matchesSearch && matchesStatus && matchesDate;
    });

    const handleOpenModal = (rental) => {
        setSelectedRental(rental);
        setNotes(rental.notes || '');
    };

    const handleSaveNotes = async () => {
        if (!selectedRental) return;
        const rentalRef = doc(db, 'rentals', selectedRental.id);
        await updateDoc(rentalRef, { notes });
        setSelectedRental(null); // Close directly
        fetchRentals();
    };

    const getPaymentStatusColor = (status) => {
        switch (status) {
            case 'Ödendi': return 'bg-green-100 text-green-800 border-green-200';
            case 'Kısmi Ödeme': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'Ödenmedi': return 'bg-red-100 text-red-800 border-red-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Modal */}
            {selectedRental && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 animate-fadeIn">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Fatura Detayı</h2>
                            <button onClick={() => setSelectedRental(null)} className="text-gray-400 hover:text-gray-600">
                                <FaTimesCircle size={24}/>
                            </button>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-xl mb-6 border border-gray-100">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-semibold">Plaka</p>
                                    <p className="font-bold text-gray-900">{selectedRental.vehiclePlate}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-semibold">Müşteri</p>
                                    <p className="font-bold text-gray-900">{selectedRental.renterName}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-semibold">Tutar</p>
                                    <p className="font-bold text-blue-600">{selectedRental.totalPrice} ₺</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-semibold">Durum</p>
                                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${getPaymentStatusColor(selectedRental.paymentStatus)}`}>
                                        {selectedRental.paymentStatus}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Notlar / Açıklama</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={4}
                                className="w-full border-gray-300 border rounded-xl p-3 focus:ring-2 focus:ring-blue-500 transition"
                                placeholder="Ödeme detayları veya özel notlar..."
                            ></textarea>
                        </div>
                        
                        <div className="mt-6 flex justify-end gap-3">
                            <button onClick={() => setSelectedRental(null)} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition">Kapat</button>
                            <button onClick={handleSaveNotes} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition flex items-center">
                                <FaSave className="mr-2"/> Kaydet
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 max-w-7xl">
                {/* Header */}
                <div className="mb-10">
                    <h1 className="text-3xl font-bold text-gray-900">Fatura Yönetimi</h1>
                    <p className="text-gray-500 mt-1">Gelir takibi ve finansal raporlar</p>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl"><FaFileInvoiceDollar size={24}/></div>
                            <span className="text-xs font-bold bg-gray-100 text-gray-600 px-2 py-1 rounded">Toplam</span>
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900">{stats.total.toLocaleString('tr-TR')} ₺</h3>
                        <p className="text-sm text-gray-500 mt-1">Toplam Ciro</p>
                    </div>

                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-green-50 text-green-600 rounded-xl"><FaCheckCircle size={24}/></div>
                            <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-1 rounded">Tahsil Edilen</span>
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900">{stats.paid.toLocaleString('tr-TR')} ₺</h3>
                        <p className="text-sm text-gray-500 mt-1">Kasa Girişi</p>
                    </div>

                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-orange-50 text-orange-600 rounded-xl"><FaClock size={24}/></div>
                            <span className="text-xs font-bold bg-orange-100 text-orange-700 px-2 py-1 rounded">Bekleyen</span>
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900">{stats.pending.toLocaleString('tr-TR')} ₺</h3>
                        <p className="text-sm text-gray-500 mt-1">Alacaklar</p>
                    </div>
                </div>

                {/* Chart Section */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-10">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">Aylık Gelir Trendi</h3>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={monthlyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorGelir" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1}/>
                                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} />
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <Tooltip 
                                    contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}}
                                    formatter={(value) => [`${value.toLocaleString()} ₺`, 'Gelir']}
                                />
                                <Area type="monotone" dataKey="amount" stroke="#3B82F6" strokeWidth={3} fillOpacity={1} fill="url(#colorGelir)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Filters & Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {/* Toolbar */}
                    <div className="p-5 border-b border-gray-100 flex flex-col lg:flex-row gap-4 justify-between items-center bg-gray-50/50">
                        <div className="flex gap-2 w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0">
                            <select 
                                value={dateFilter} 
                                onChange={(e) => setDateFilter(e.target.value)} 
                                className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5"
                            >
                                <option value="all">Tüm Tarihler</option>
                                <option value="thisMonth">Bu Ay</option>
                                <option value="lastMonth">Geçen Ay</option>
                                <option value="thisYear">Bu Yıl</option>
                            </select>

                            <select 
                                value={filterStatus} 
                                onChange={(e) => setFilterStatus(e.target.value)} 
                                className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5"
                            >
                                <option value="Tümü">Tüm Durumlar</option>
                                <option value="Ödendi">Ödendi</option>
                                <option value="Kısmi Ödeme">Kısmi Ödeme</option>
                                <option value="Ödenmedi">Ödenmedi</option>
                            </select>
                        </div>

                        <div className="relative w-full lg:w-72">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"/>
                            <input 
                                type="text" 
                                placeholder="Plaka veya müşteri ara..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition shadow-sm"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-20">
                            <FaSpinner className="animate-spin inline-block text-3xl text-blue-600 mb-4"/>
                            <p className="text-gray-500">Veriler yükleniyor...</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-4 font-semibold">Plaka</th>
                                        <th className="px-6 py-4 font-semibold">Kiralayan</th>
                                        <th className="px-6 py-4 font-semibold">Tarih Aralığı</th>
                                        <th className="px-6 py-4 font-semibold">Tutar</th>
                                        <th className="px-6 py-4 font-semibold text-center">Durum</th>
                                        <th className="px-6 py-4 font-semibold text-right">İşlem</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredRentals.map(rental => (
                                        <tr key={rental.id} className="hover:bg-gray-50 transition">
                                            <td className="px-6 py-4 font-mono font-medium text-gray-900">{rental.vehiclePlate}</td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900">{rental.renterName}</div>
                                                <div className="text-xs text-gray-500">{rental.renterPhone}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-xs text-gray-600 flex items-center">
                                                    <FaCalendarAlt className="mr-1.5 text-gray-400" size={10}/>
                                                    {rental.rentalStartDate?.toDate().toLocaleDateString()}
                                                </div>
                                                <div className="text-xs text-gray-400 mt-1 ml-4">
                                                    {rental.rentalEndDate?.toDate().toLocaleDateString()}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 font-bold text-gray-800">
                                                {Number(rental.totalPrice).toLocaleString('tr-TR')} ₺
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getPaymentStatusColor(rental.paymentStatus)}`}>
                                                    {rental.paymentStatus}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button 
                                                    onClick={() => handleOpenModal(rental)} 
                                                    className="text-blue-600 hover:text-blue-900 text-sm font-medium hover:underline"
                                                >
                                                    Yönet
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredRentals.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center py-12 text-gray-500">
                                                Kayıt bulunamadı.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompanyBillingPage;